import { createRoute } from '@tanstack/react-router';
import { BarChart3, Package, DollarSign, Layers, FolderTree } from 'lucide-react';

import { Shell } from '../components/layout/Shell';
import { PlatformPieChart } from '../components/charts/PlatformPieChart';
import { ConditionBarChart } from '../components/charts/ConditionBarChart';
import { AcquisitionLineChart } from '../components/charts/AcquisitionLineChart';
import { ValueByCategoryChart } from '../components/charts/ValueByCategoryChart';
import { useCollectionStats } from '../hooks/useStats';
import { useVault } from '../context/VaultContext';
import { useTranslation } from '../i18n';
import { Route as rootRoute } from './__root';
import styles from './stats.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stats',
  component: StatsPage,
});

function StatsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useCollectionStats();
  const { vaultTypeDef } = useVault();

  // Dynamic label: "Platforms" for retro_computers, "Brands" for others
  const platformLabel = vaultTypeDef && vaultTypeDef.id !== 'retro_computers'
    ? 'Brands'
    : t('stats.uniquePlatforms');

  return (
    <Shell>
      <div className={styles.page}>
        {isLoading ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : !data || data.summary.totalItems === 0 ? (
          <div className={styles.emptyState}>
            <BarChart3 size={48} strokeWidth={1} />
            <div className={styles.emptyTitle}>{t('stats.emptyTitle')}</div>
            <div className={styles.emptyDesc}>{t('stats.emptyDesc')}</div>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className={styles.summaryGrid}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryIcon}><Package size={16} strokeWidth={1.5} /></div>
                <div className={styles.summaryLabel}>{t('stats.totalItems')}</div>
                <div className={styles.summaryValue}>{data.summary.totalItems}</div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryIcon}><DollarSign size={16} strokeWidth={1.5} /></div>
                <div className={styles.summaryLabel}>{t('stats.totalValue')}</div>
                <div className={styles.summaryValue}>
                  ${parseFloat(data.summary.totalValue).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryIcon}><Layers size={16} strokeWidth={1.5} /></div>
                <div className={styles.summaryLabel}>{platformLabel}</div>
                <div className={styles.summaryValue}>{data.summary.uniquePlatforms}</div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryIcon}><FolderTree size={16} strokeWidth={1.5} /></div>
                <div className={styles.summaryLabel}>{t('stats.uniqueCategories')}</div>
                <div className={styles.summaryValue}>{data.summary.uniqueCategories}</div>
              </div>
            </div>

            {/* Charts */}
            <div className={styles.chartsGrid}>
              <PlatformPieChart
                data={data.platformDistribution}
                title={vaultTypeDef && vaultTypeDef.id !== 'retro_computers' ? 'Brand Distribution' : undefined}
              />
              <ConditionBarChart data={data.conditionBreakdown} />
              <AcquisitionLineChart data={data.acquisitionTimeline} />
              <ValueByCategoryChart data={data.valueByCategory} />
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}
